import { useEffect, useState } from "react";
import api, { formatINR } from "../lib/api";
import { X, Plus, Trash2, Pencil, Users, IndianRupee } from "lucide-react";
import { toast } from "sonner";

const blank = { name: "", upi_id: "", phone: "", category: "", notes: "" };

export function PayeeManager({ open, onClose, onChanged }) {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(blank);
  const [editId, setEditId] = useState(null);

  async function load() {
    try { const { data } = await api.get("/payees"); setRows(data || []); } catch { /* ignore */ }
  }
  useEffect(() => { if (open) { load(); setForm(blank); setEditId(null); } }, [open]);

  if (!open) return null;

  async function save(e) {
    e.preventDefault();
    if (!(form.name.trim() || form.upi_id.trim() || form.phone.trim())) { toast.error("Add a name, UPI ID or phone"); return; }
    try {
      if (editId) await api.put(`/payees/${editId}`, form);
      else await api.post("/payees", form);
      toast.success(editId ? "Payee updated" : "Payee added");
      setForm(blank); setEditId(null); load(); onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save payee"); }
  }
  function edit(p) { setForm({ name: p.name || "", upi_id: p.upi_id || "", phone: p.phone || "", category: p.category || "", notes: p.notes || "" }); setEditId(p.id); }
  async function remove(p) {
    if (!confirm(`Delete favourite payee "${p.name || p.upi_id || p.phone}"?`)) return;
    try { await api.delete(`/payees/${p.id}`); toast.success("Payee deleted"); load(); onChanged && onChanged(); }
    catch { toast.error("Could not delete"); }
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-[60] flex items-start justify-center overflow-y-auto py-8" data-testid="payee-manager" onClick={onClose}>
      <div className="bg-[hsl(var(--surface))] text-[hsl(var(--text-main))] w-full max-w-2xl mx-4 rounded-sm border border-[hsl(var(--border))]" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-[hsl(var(--border))] flex items-center justify-between">
          <div className="flex items-center gap-2"><Users className="w-5 h-5 text-[#C84B31]" /><h2 className="font-headings text-lg font-bold">Favourite Payees</h2></div>
          <button className="btn-ghost" onClick={onClose} data-testid="payee-manager-close"><X className="w-4 h-4" /></button>
        </div>

        <form onSubmit={save} className="p-6 border-b border-[hsl(var(--border))]">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input className="input-field" placeholder="Payee name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="payee-name-input" />
            <input className="input-field font-mono" placeholder="UPI ID (name@bank)" value={form.upi_id} onChange={(e) => setForm({ ...form, upi_id: e.target.value })} data-testid="payee-upi-input" />
            <input className="input-field" placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="payee-phone-input" />
            <input className="input-field" placeholder="Category (Driver / Broker / Fuel)" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="payee-category-input" />
          </div>
          <div className="flex gap-2 mt-3">
            <button type="submit" className="btn-primary" data-testid="payee-save-button"><Plus className="w-4 h-4" /> {editId ? "Update payee" : "Add payee"}</button>
            {editId && <button type="button" className="btn-ghost" onClick={() => { setForm(blank); setEditId(null); }}>Cancel</button>}
          </div>
        </form>

        <div className="max-h-[50vh] overflow-y-auto">
          <table className="w-full lrs-table">
            <thead><tr>
              <th className="table-th">Payee</th>
              <th className="table-th">UPI / Phone</th>
              <th className="table-th nowrap text-right">Times paid</th>
              <th className="table-th nowrap text-right">Total paid</th>
              <th className="table-th nowrap text-right"></th>
            </tr></thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td className="table-td text-[#68665E] text-center" colSpan={5}>No favourite payees yet. Add one above, or tick “Save as favourite payee” when paying.</td></tr>
              ) : rows.map((p) => (
                <tr key={p.id} className="table-row" data-testid={`payee-row-${p.id}`}>
                  <td className="table-td"><div className="font-medium">{p.name || "—"}</div>{p.category ? <div className="text-xs text-[hsl(var(--text-muted))]">{p.category}</div> : null}</td>
                  <td className="table-td font-mono text-xs break-all">{p.upi_id || (p.phone ? `Ph: ${p.phone}` : "—")}</td>
                  <td className="table-td nowrap text-right data-num">{p.payment_count || 0}</td>
                  <td className="table-td nowrap text-right data-num font-medium">{formatINR(p.total_paid || 0)}</td>
                  <td className="table-td nowrap text-right">
                    <button className="btn-ghost" title="Edit" onClick={() => edit(p)} data-testid={`payee-edit-${p.id}`}><Pencil className="w-4 h-4" /></button>
                    <button className="btn-ghost text-[#8A2526]" title="Delete" onClick={() => remove(p)} data-testid={`payee-delete-${p.id}`}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
